import type { CameraState, Vec3 } from '@v2/contracts'

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))
const mix = (a: number, b: number, weight: number) => a + (b - a) * weight
export const DEFAULT_CAMERA: CameraState = {
  target: [0, 0, 0], azimuth: 0, elevation: 1.03, distance: 17.5, fov: 0.72,
}

/** Camera owns only navigation. It never submits a game command. */
export class TableCamera {
  private current: CameraState = { ...DEFAULT_CAMERA }
  private destination: CameraState = this.current
  private navigation = 0
  owner: 'overview' | 'user' | 'focus' = 'overview'

  get state(): CameraState { return this.current }
  /** Changes for every navigation request, allowing automatic cues to yield to input. */
  get navigationVersion(): number { return this.navigation }

  capture(): CameraSnapshot { return { view: this.destination, owner: this.owner } }

  moveTo(view: CameraState, immediate = false, owner: CameraSnapshot['owner'] = 'focus'): void {
    this.navigation += 1
    this.owner = owner
    this.destination = view
    if (immediate) this.current = view
  }

  /** Stop an automatic flight at the visible view without jumping or returning. */
  stop(): void { this.navigation += 1; this.destination = this.current }

  overview(aspect = 1.5, immediate = false, bounds?: { center: Vec3; width: number; depth: number }): void {
    this.navigation += 1
    this.owner = 'overview'
    const halfWidth = (bounds?.width ?? 17) / 2
    const halfDepth = (bounds?.depth ?? 12) / 2
    const span = Math.tan(DEFAULT_CAMERA.fov / 2)
    const closestEdge = halfDepth * Math.cos(DEFAULT_CAMERA.elevation)
    const distance = Math.max(halfWidth / (span * Math.max(0.3, aspect)), halfDepth * Math.sin(DEFAULT_CAMERA.elevation) / span) + closestEdge
    this.destination = { ...DEFAULT_CAMERA, target: bounds?.center ?? DEFAULT_CAMERA.target, distance: distance * 1.06 }
    if (immediate) this.current = this.destination
  }

  focus(position: Vec3, size: number | Vec3, aspect = 1.5, rotation = 0): void {
    this.navigation += 1
    this.owner = 'focus'
    const elevation = Math.max(0.8, this.current.elevation)
    const dimensions: Vec3 = typeof size === 'number' ? [size, 0, size] : size
    this.destination = {
      ...this.current,
      target: position,
      elevation,
      distance: Math.max(5.5, focusDistance(dimensions, rotation, this.current.azimuth, elevation, this.current.fov, aspect)),
    }
  }

  orbit(dx: number, dy: number): void {
    this.navigation += 1
    this.owner = 'user'
    this.current = {
      ...this.current,
      azimuth: this.current.azimuth - dx * 0.007,
      elevation: clamp(this.current.elevation + dy * 0.005, 0.35, Math.PI / 2 - 0.015),
    }
    this.destination = this.current
  }

  /** Drag the table under the pointer, in camera-relative ground-plane coordinates. */
  pan(dx: number, dy: number, viewportHeight = 600): void {
    const height = Math.max(1, viewportHeight)
    const start = pointerOnPlane(this.current, height / 2, height / 2, height, height, this.current.target[1])
    const end = pointerOnPlane(this.current, height / 2 + dx, height / 2 + dy, height, height, this.current.target[1])
    if (!start || !end) return
    this.navigation += 1
    this.owner = 'user'
    this.current = {
      ...this.current,
      target: [this.current.target[0] + start[0] - end[0], this.current.target[1], this.current.target[2] + start[2] - end[2]],
    }
    this.destination = this.current
  }

  /** Read the current area directly overhead without losing the player's chosen target. */
  topView(): void {
    this.navigation += 1
    this.owner = 'user'
    this.destination = { ...this.current, elevation: Math.PI / 2 - 0.015 }
  }

  zoom(delta: number, anchor?: { x: number; y: number; width: number; height: number }): void {
    this.navigation += 1
    const distance = this.owner === 'user' ? this.destination.distance : this.current.distance
    this.owner = 'user'
    this.destination = {
      ...this.current,
      distance: clamp(distance * Math.exp(delta * 0.001), 4.5, 160),
    }
    if (anchor) {
      const before = pointerOnPlane(this.current, anchor.x, anchor.y, anchor.width, anchor.height, this.current.target[1])
      const after = pointerOnPlane(this.destination, anchor.x, anchor.y, anchor.width, anchor.height, this.current.target[1])
      if (before && after) this.destination = { ...this.destination, target: [
        this.current.target[0] + before[0] - after[0], this.current.target[1], this.current.target[2] + before[2] - after[2],
      ] }
    }
  }

  tick(seconds: number): CameraState {
    const weight = 1 - Math.exp(-Math.min(seconds, 0.1) * 12)
    this.current = {
      ...this.current,
      target: this.current.target.map((value, i) => mix(value, this.destination.target[i], weight)) as unknown as Vec3,
      azimuth: mix(this.current.azimuth, this.destination.azimuth, weight),
      elevation: mix(this.current.elevation, this.destination.elevation, weight),
      distance: mix(this.current.distance, this.destination.distance, weight),
    }
    return this.current
  }
}

/** Fit every box corner, including the nearer edge's perspective enlargement. */
export function focusDistance(size: Vec3, rotation: number, azimuth: number, elevation: number, fov: number, aspect: number): number {
  const sinA = Math.sin(azimuth), cosA = Math.cos(azimuth)
  const sinE = Math.sin(elevation), cosE = Math.cos(elevation)
  const sinR = Math.sin(rotation), cosR = Math.cos(rotation)
  // Leave breathing room around the piece or zone without treating its full width as a radius.
  const verticalSpan = Math.tan(fov / 2) * 0.88
  const horizontalSpan = verticalSpan * Math.max(Number.EPSILON, aspect)
  let distance = 0
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) {
    const localX = sx * size[0] / 2, y = sy * size[1] / 2, localZ = sz * size[2] / 2
    const x = cosR * localX + sinR * localZ
    const z = -sinR * localX + cosR * localZ
    const right = cosA * x - sinA * z
    const up = -sinA * sinE * x + cosE * y - cosA * sinE * z
    const towardEye = sinA * cosE * x + sinE * y + cosA * cosE * z
    distance = Math.max(distance, towardEye + Math.abs(right) / horizontalSpan, towardEye + Math.abs(up) / verticalSpan)
  }
  return distance
}

export interface CameraSnapshot {
  readonly view: CameraState
  readonly owner: 'overview' | 'user' | 'focus'
}

/** Project a pointer onto a horizontal carrying plane using the same camera convention as the GPU. */
export function pointerOnPlane(camera: CameraState, x: number, y: number, width: number, height: number, planeY: number): Vec3 | null {
  if (width <= 0 || height <= 0) return null
  const { azimuth: a, elevation: e, distance: d, target } = camera
  const sinA = Math.sin(a), cosA = Math.cos(a), sinE = Math.sin(e), cosE = Math.cos(e)
  const eye: Vec3 = [target[0] + sinA * cosE * d, target[1] + sinE * d, target[2] + cosA * cosE * d]
  const forward: Vec3 = [-sinA * cosE, -sinE, -cosA * cosE]
  const right: Vec3 = [cosA, 0, -sinA]
  const up: Vec3 = [-sinA * sinE, cosE, -cosA * sinE]
  const span = Math.tan(camera.fov / 2)
  const u = (x / width * 2 - 1) * span * width / height
  const v = (1 - y / height * 2) * span
  const direction: Vec3 = [forward[0] + right[0] * u + up[0] * v, forward[1] + up[1] * v, forward[2] + right[2] * u + up[2] * v]
  if (Math.abs(direction[1]) < 1e-6) return null
  const t = (planeY - eye[1]) / direction[1]
  if (t < 0) return null
  return [eye[0] + direction[0] * t, planeY, eye[2] + direction[2] * t]
}
